/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790208000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Sep 24 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "netsuite-rest",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "REST vs SOAP",
          "depth": 0,
          "id": "rest-vs-soap"
        }, {
          "value": "Setup in NetSuite",
          "depth": 0,
          "id": "setup-in-netsuite"
        }, {
          "value": "Create an integration",
          "depth": 1,
          "id": "create-an-integration"
        }, {
          "value": "Create a role",
          "depth": 1,
          "id": "create-a-role"
        }, {
          "value": "Create a user",
          "depth": 1,
          "id": "create-a-user"
        }, {
          "value": "Create an access token",
          "depth": 1,
          "id": "create-an-access-token"
        }, {
          "value": "Enable REST web services",
          "depth": 1,
          "id": "enable-rest-web-services"
        }, {
          "value": "Connect to NetSuite",
          "depth": 0,
          "id": "connect-to-netsuite"
        }, {
          "value": "Authenticate with basic auth",
          "depth": 1,
          "id": "authenticate-with-basic-auth"
        }, {
          "value": "Authenticate with OAuth",
          "depth": 1,
          "id": "authenticate-with-oauth"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing entities",
          "depth": 1,
          "id": "syncing-entities"
        }, {
          "value": "Transforming records",
          "depth": 1,
          "id": "transforming-records"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      h3: "h3",
      ol: "ol",
      img: "img",
      code: "code",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PremiumDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Netsuite"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Check out our\n", _jsx(_components.a, {
            href: "https://hightouch.com/playbooks/automating-usage-based-billing",
            children: "playbook"
          }), " and\n", _jsx(_components.a, {
            href: "https://hightouch.com/blog/automated-billing",
            children: "blog post"
          }), " to learn how to\nautomate usage-based billing to streamline your finance operations."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Entities"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to entities in NetSuite, for example, Customers, Invoices, Sales Orders, and Journal Entries"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://system.netsuite.com/help/helpcenter/en_US/APIs/REST_API_Browser/record/v1/2026.1/index.html",
                children: "NetSuite schema browser"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Transforms"
              })
            }), _jsx(_components.td, {
              children: "Transform records from one type into another, using data from existing records."
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.oracle.com/en/cloud/saas/netsuite/ns-online-help/section_4267258715.html",
                children: "NetSuite record transform"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "rest-vs-soap",
        children: "REST vs SOAP"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch NetSuite REST destination offers a few advantages over the SOAP destination:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The NetSuite REST API uses JSON for web service requests and responses, which is easier to read and debug in the ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "live debugger"
          }), " compared to XML."]
        }), "\n", _jsx(_components.li, {
          children: "Hightouch prioritizes supporting the latest REST API features and versions."
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.a, {
            href: "#field-mapping",
            children: "NetSuite REST"
          }), " destination supports the ", _jsx(_components.a, {
            href: "/syncs/mapping-data#inline-mapping",
            children: "inline mapper"
          }), " to build objects and arrays"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Unless you have a particular entity that's only supported by the SOAP API, we recommend using the REST NetSuite destination."
      }), "\n", _jsx(_components.h2, {
        id: "setup-in-netsuite",
        children: "Setup in NetSuite"
      }), "\n", _jsx(_components.p, {
        children: "Before you can use Hightouch to sync data to NetSuite, you need to create and configure a NetSuite integration.\nThen, to grant access to Hightouch to synchronize data between your source and NetSuite, you need to create a role, a user, and an authentication token for the integration."
      }), "\n", _jsxs(_components.p, {
        children: ["These steps are the same whether you choose to ", _jsx(_components.a, {
          href: "#connect-to-netsuite",
          children: "connect Hightouch to NetSuite"
        }), " with basic authentication or OAuth.\nThe only difference is in the final step of ", _jsx(_components.a, {
          href: "#enable-rest-web-services",
          children: "enabling web services"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Depending on your NetSuite account's setup, you may need an ", _jsx(_components.strong, {
            children: "admin"
          }), " role to\ncomplete setup in NetSuite. Netsuite permission changes to a role or user\ndon't occur immediately. These changes can take a few minutes or hours to go\ninto effect."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-an-integration",
        children: "Create an integration"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Setup > Integration > Manage Integrations"
          }), " or use the global search to find and go to the ", _jsx(_components.strong, {
            children: "Manage Integrations"
          }), " page."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-manage-integrations.png",
          alt: "Global search in NetSuite"
        })
      }), "\n", _jsx(_components.p, {
        children: "This page shows a list of enabled integrations for your account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-integrations-page.png",
          alt: "Integrations page in NetSuite"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "New"
          }), " to create a new integration with the following properties:"]
        }), "\n"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.strong, {
            children: "Name"
          }), " that can easily be associated with Hightouch, for example, \"Hightouch Integration\""]
        }), "\n", _jsxs(_components.li, {
          children: ["Set ", _jsx(_components.strong, {
            children: "State"
          }), " to ", _jsx(_components.strong, {
            children: "Enabled"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Check ", _jsx(_components.strong, {
            children: "Token-Based Authentication"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Uncheck ", _jsx(_components.strong, {
            children: "TBA:Authorization flow"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Uncheck ", _jsx(_components.strong, {
            children: "Authorization Code Grant"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Leave other authentication options unchanged."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-new-integration.png",
          alt: "Integration creation in NetSuite"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Scroll to the bottom of the page and click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Scroll to ", _jsx(_components.strong, {
            children: "Client credentials"
          }), " and Copy and store the ", _jsx(_components.strong, {
            children: "Consumer Key / Client ID"
          }), " and ", _jsx(_components.strong, {
            children: "Consumer Secret / Client secret"
          }), " in a secure location. You'll need them to connect to Hightouch."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-integration-credentials.png",
          alt: "Integration credentials in NetSuite"
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-a-role",
        children: "Create a role"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Setup > Users/Roles > Manage Roles"
          }), " and click ", _jsx(_components.strong, {
            children: "New"
          }), " or use the global search to find and go to the ", _jsx(_components.strong, {
            children: "New Role"
          }), " page."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-new-role.png",
          alt: "Global search in NetSuite"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsx(_components.li, {
          children: "Create a role with the following properties:"
        }), "\n"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A descriptive ", _jsx(_components.strong, {
            children: "Name"
          }), ", for example, \"Hightouch Role.\""]
        }), "\n", _jsxs(_components.li, {
          children: ["Check ", _jsx(_components.strong, {
            children: "Do not restrict employee fields"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["For multi-subsidiary NetSuite users, check ", _jsx(_components.strong, {
            children: "All"
          }), " under ", _jsx(_components.strong, {
            children: "Accessible subsidiaries"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Authentication"
          }), ", check ", _jsx(_components.strong, {
            children: "Web Services only Role"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-new-role-2.png",
          alt: "New role in NetSuite"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Add the following permissions under ", _jsx(_components.strong, {
            children: "Permissions > Setup"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Access Token Management: Full"
            }), "\n", _jsx(_components.li, {
              children: "Custom Body Fields: Full"
            }), "\n", _jsx(_components.li, {
              children: "Custom Column Fields: Full"
            }), "\n", _jsx(_components.li, {
              children: "Custom Entity Fields: Full"
            }), "\n", _jsx(_components.li, {
              children: "Custom Fields: Full"
            }), "\n", _jsx(_components.li, {
              children: "Custom Item Fields: Full"
            }), "\n", _jsx(_components.li, {
              children: "REST Web Services: Full"
            }), "\n", _jsx(_components.li, {
              children: "User Access Tokens: Full"
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-role-permissions.png",
          alt: "Role permissions"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Permissions > Lists"
          }), " and ", _jsx(_components.strong, {
            children: "Permissions > Transactions"
          }), ", provide ", _jsx(_components.strong, {
            children: "Full"
          }), " permissions for the NetSuite entities you plan on syncing to. For example, if you want to update sales orders, you need to go to the ", _jsx(_components.strong, {
            children: "Transactions"
          }), " tab and select ", _jsx(_components.strong, {
            children: "Sales Order"
          }), " from the list. If you want to update accounts, you need to go to ", _jsx(_components.strong, {
            children: "Lists"
          }), " and select ", _jsx(_components.strong, {
            children: "Accounts"
          }), " from the list."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-accounts-permissions.png",
          alt: "Role permissions"
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-a-user",
        children: "Create a user"
      }), "\n", _jsx(_components.p, {
        children: "Now you can create a new user that uses this role."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.strong, {
            children: "Manage Users"
          }), " page. Create a new user or select the user you will be using Hightouch with."]
        }), "\n", _jsxs(_components.li, {
          children: ["For new users: fill the ", _jsx(_components.strong, {
            children: "Employee"
          }), " form with all required fields."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Access"
          }), " tab, check ", _jsx(_components.strong, {
            children: "Manually assign or change password"
          }), " and assign a secure password."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Roles"
          }), " Assign the role, for example, \"Hightouch Role,\" you previously created."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-new-user.png",
          alt: "New user"
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-an-access-token",
        children: "Create an access token"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.strong, {
            children: "Setup > Users/Roles > Access Tokens"
          }), " page, or use the global search to find it."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "New Access Token"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-new-access-token.png",
          alt: "New access token"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsx(_components.li, {
          children: "Select the integration, user, and role you previously configured."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-new-access-token-2.png",
          alt: "New access token"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["When you click ", _jsx(_components.strong, {
            children: "Save"
          }), ", NetSuite displays a ", _jsx(_components.strong, {
            children: "Token ID"
          }), " and a ", _jsx(_components.strong, {
            children: "Token Secret"
          }), ". Copy and store them in a secure location. You'll need them to connect to Hightouch."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-new-access-token-3.png",
          alt: "New access token"
        })
      }), "\n", _jsx(_components.h3, {
        id: "enable-rest-web-services",
        children: "Enable REST web services"
      }), "\n", _jsxs(_components.p, {
        children: ["Finally, go to ", _jsx(_components.strong, {
          children: "Setup > Company > Enable Features > SuiteTalk (Web Services)"
        }), " and enable ", _jsx(_components.strong, {
          children: "REST Web Services"
        }), ". Once you check this box, NetSuite prompts you to agree to terms of service."]
      }), "\n", _jsxs(_components.p, {
        children: ["On the same page, under ", _jsx(_components.strong, {
          children: "Manage Authentication"
        }), ", enable either :"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Token Based Authentication"
          }), ", if you plan on ", _jsx(_components.a, {
            href: "#connect-to-netsuite",
            children: "connecting Hightouch"
          }), " to NetSuite with basic authentication"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "OAuth 2.0"
          }), ", if you plan on ", _jsx(_components.a, {
            href: "#connect-to-netsuite",
            children: "connecting Hightouch"
          }), " to NetSuite with OAuth"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you're not sure which you will use, you can check both of them."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-rest-webservices.png",
          alt: "Enable REST Web services and Token-based authentication"
        })
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-netsuite",
        children: "Connect to NetSuite"
      }), "\n", _jsxs(_components.p, {
        children: ["You're now ready to create a NetSuite destination in Hightouch. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "NetSuite"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to NetSuite via ", _jsx(_components.strong, {
          children: "Basic Auth"
        }), " or ", _jsx(_components.strong, {
          children: "OAuth"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "OAuth only requires you to login to your NetSuite account rather than enter credentials."
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-basic-auth",
        children: "Authenticate with basic auth"
      }), "\n", _jsx(_components.p, {
        children: "Basic auth requires you to enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Account ID"
          }), ": You can find this on your ", _jsx(_components.strong, {
            children: "Company information"
          }), " page in NetSuite."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Consumer Key"
          }), ": From your new integration"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Consumer Secret"
          }), ": From your new integration"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Token Key"
          }), ": From your new access token"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Token Secret"
          }), ": From your new access token"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If your account is a Sandbox account, you need to include the suffix ", _jsx(_components.code, {
          children: "_SB<number>"
        }), " as part of the ", _jsx(_components.strong, {
          children: "Account ID"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "SB"
          }), " should be in uppercase."]
        }), "\n", _jsxs(_components.li, {
          children: ["The Sandbox suffix should be included with an underscore (", _jsx(_components.code, {
            children: "_"
          }), ")."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if the Account ID in your sandbox is ", _jsx(_components.code, {
          children: "1234567-sb1"
        }), ", then you need to set your Account ID as ", _jsx(_components.code, {
          children: "1234567_SB1"
        }), " in your sync configuration."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-oauth",
        children: "Authenticate with OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "OAuth"
        }), " and log into your NetSuite account. Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your NetSuite destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the NetSuite destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-entities",
        children: "Syncing entities"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch NetSuite destination allows you to sync data to supported NetSuite entities, for example, customers, invoices, vendors, etc. Hightouch also supports syncing custom records types."
      }), "\n", _jsx(_components.p, {
        children: "The specific entities you can sync to depend on your NetSuite implementation. You can view your available entities in the dropdown. Because of the large catalog size, it's best to search for the entities you're interested in."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-rest-available-entities.png",
          alt: "Available entities in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match source rows to entities in Netsuite, you need to select a source column and corresponding Netsuite field.\nDepending on the sync mode you select, you can match on the following Netsuite fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Internal ID"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Update"
        }), " mode supports both, whereas ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode only supports ", _jsx(_components.strong, {
          children: "External ID"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-rest-record-matching.png",
          alt: "Record matching in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, NetSuite automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to NetSuite default and custom fields."
      }), "\n", _jsx(_components.p, {
        children: "Mappings mirror the NetSuite REST schema. That means some of your model columns might need to be JSON objects."
      }), "\n", _jsxs(_components.p, {
        children: ["NetSuite generally expects JSON objects when an entity references another entity. For example, in an ", _jsx(_components.a, {
          href: "https://system.netsuite.com/help/helpcenter/en_US/APIs/REST_API_Browser/record/v1/2026.1/index.html#/definitions/invoice",
          children: "invoice"
        }), ", the ", _jsx(_components.code, {
          children: "entity"
        }), " field can reference a customer, partner, vendor, employee, contact, etc. by their ID. To make that reference, you need to map a JSON object like this to the ", _jsx(_components.code, {
          children: "entity"
        }), " field:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can use also external IDs for references, for example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"externalId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/mapping-data#inline-mapping",
          children: "inline mapper"
        }), " is purpose-built to help you create ", _jsx(_components.a, {
          href: "/syncs/mapping-data#create-an-object",
          children: "JSON objects"
        }), " like this."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-netsuite-rest-field-mapping.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The inline mapper can also help you create ", _jsx(_components.a, {
          href: "/syncs/mapping-data#create-an-array",
          children: "arrays"
        }), ".\nNetSuite arrays are often wrapped in a JSON object.\nFor example, an invoice supports an ", _jsx(_components.code, {
          children: "item"
        }), " field that expects an array of items:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"items\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"item\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"8\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"line\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"-1\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/mapping-data#create-an-array",
          children: "inline mapping docs"
        }), " to learn how to create an array like this and what the ", _jsx(_components.a, {
          href: "/syncs/mapping-data#required-data-format",
          children: "required data format"
        }), " for your model is. To see the schema that NetSuite expects for different entities, go to the ", _jsx(_components.a, {
          href: "https://system.netsuite.com/help/helpcenter/en_US/APIs/REST_API_Browser/record/v1/2026.1/index.html",
          children: "REST browser"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "overwrite-sublists",
        children: "Overwrite sublists"
      }), "\n", _jsx(_components.p, {
        children: "Updating sublist properties can result in appending the mapped items to the record's existing sublist.\nThis may result in unwanted duplicates. You can explicitly specify in Hightouch to overwrite your\nmapped sublist items in NetSuite. Overwriting the sublist removes all existing items and updates\nit with incoming data."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the record in NetSuite with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced record from your NetSuite instance"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "transforming-records",
        children: "Transforming records"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch NetSuite destination allows you to transform records from one type to another, for example, transforming purchase orders into vendor bills."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match source rows to entities in Netsuite, you need to select a source column and corresponding Netsuite field.\nThe ID must be the ID of the original record that will be transformed. You may use the following IDs to match on:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Internal ID"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to NetSuite default and custom fields of the target record type."
      }), "\n", _jsx(_components.p, {
        children: "Mappings mirror the NetSuite REST schema. That means some of your model columns might need to be JSON objects."
      }), "\n", _jsxs(_components.p, {
        children: ["NetSuite generally expects JSON objects when an entity references another entity. For example, in an ", _jsx(_components.a, {
          href: "https://system.netsuite.com/help/helpcenter/en_US/APIs/REST_API_Browser/record/v1/2026.1/index.html#/definitions/invoice",
          children: "invoice"
        }), ", the ", _jsx(_components.code, {
          children: "entity"
        }), " field can reference a customer, partner, vendor, employee, contact, etc. by their ID. To make that reference, you need to map a JSON object like this to the ", _jsx(_components.code, {
          children: "entity"
        }), " field:"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
